import { AnimatePresence, motion } from "motion/react";
import { FC, useEffect, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  FiltersActions,
  FloatingMenuGrid,
  TableWrapperGeneric,
  useTableColumnsDynamic,
} from "@ryde/controls";
import {
  BulkUpdateFormDrawer,
  ClientSideBulkOperationDrawerFlow,
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  TableSettingsDrawerWrapperGeneric,
} from "@ryde/layout";
import {
  IPageHeaderContext,
  PageHeaderContext,
  useDynamicFormsFetch,
  usePageContext,
  usePageTitle,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  bulkUpdateFormDrawerAF,
  IColumnDef,
  IExtendableDto,
  ITableDto,
  Log,
  selectedTableRowsIdsAtomFamily,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import { AreaHeaderRightActions } from "./area.header-right-actions";
import { AREAS_MAP_INITIAL_WIDTH } from "./areas.constants";
import { useAreasGridSplitter } from "./hooks";
import {
  AreasGridMap,
  AreasGridTableWrapperStyled,
  AreasPanelTop,
} from "./panels";

export const AreasPageContent: FC = () => {
  const { entity } = usePageContext();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily(entity));
  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );
  const { initDynamicGridFetch, fetchTableRows } = useDynamicFormsFetch(entity);
  const { tableDefsGeneric } = useTableColumnsDynamic();
  const { pageTitleTranslated } = usePageTitle();

  const onFiltersApply = useEvent((): void => {
    fetchTableRows();
  });

  const headerContextValue: IPageHeaderContext = useMemo(() => {
    return {
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  useEffect(() => {
    initDynamicGridFetch();
    Log.debug(
      "calendarDates changed for client-side Date filters",
      calendarDates,
    );
  }, [initDynamicGridFetch, calendarDates]);

  const {
    mapOpen,
    x,
    onDragStartHandler,
    onDragEndHandler,
    gridSizeProps,
    mapSizeProps,
    dragConstraints,
  } = useAreasGridSplitter(AREAS_MAP_INITIAL_WIDTH);

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={pageTitleTranslated}
        customHeaderBottomRightActions={<AreaHeaderRightActions />}
        bottomWidgetComponent={<AreasPanelTop />}
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <PageHeaderFilters Actions={<FiltersActions />} />
        </PageHeaderContext.Provider>
      </PageHeader>

      <AreasGridTableWrapperStyled>
        <motion.div className="grid_panel thin_scrollbar" style={gridSizeProps}>
          <TableWrapperGeneric
            tableDefs={
              tableDefsGeneric as IColumnDef<IExtendableDto, unknown>[]
            }
          />
        </motion.div>

        <AnimatePresence>
          {mapOpen && (
            <motion.div
              className="splitter"
              key="splitter"
              dragElastic={0}
              dragMomentum={false}
              onDragStart={onDragStartHandler}
              onDragEnd={onDragEndHandler}
              dragConstraints={dragConstraints}
              drag="x"
              style={{
                cursor: "col-resize",
                x,
              }}
            />
          )}

          {mapOpen && (
            <motion.div
              key="map_panel"
              className="map_panel"
              style={mapSizeProps}
            >
              <AreasGridMap />
            </motion.div>
          )}
        </AnimatePresence>
      </AreasGridTableWrapperStyled>

      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<ITableDto>
          tableDefs={tableDefsGeneric}
        />
      )}
      {selectedIds && <FloatingMenuGrid />}
      {bulkUpdateFormDrawerOpened && <BulkUpdateFormDrawer />}

      {bulkOperationsDrawerOpened && <ClientSideBulkOperationDrawerFlow />}
    </>
  );
};
